import { Card, Skeleton, Tabs } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { formatDate } from "../../services/util";
import CustomHeader from "../common/Header";
import TicketByStatus from "./TicketByStatus";
import TicketByMonth from "./TicketByMonth";
import BugByMonth from "./BugByMonth";
import AssigneeTimeLog from "./AssigneeTimeLog";
import AssigneeAvgTimeLog from "./AssigneeAvgTimeLog";
import TicketByGroup from "./TicketByGroup";
import BugByProject from "./BugByProject";
import TicketByProject from "./TicketByProject";
import TicketByAge from "./TicketByAge";
import { HBarChart } from "./chartUtils";
import "./JiraDashboard.css";

const { TabPane } = Tabs;

function JiraDashboard() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const navigate = useNavigate();
    const { selectedDates } = useContext(SettingContext);
    const [assignees_tickets, setAssigneesTickets] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);

    useEffect(() => {
        async function fetch() {
            const { data: jiraProjects } = await HTTP(
                "/analytics/getJiraProjects",
                { staff_id },
            );
            setProjectOptions(jiraProjects);

            const { data: jiraData } = await HTTP(
                "/analytics/getJiraDashboard",
                {
                    staff_id,
                    start_date: formatDate(selectedDates[0]),
                    end_date: formatDate(selectedDates[1]),
                },
            );

            setAssigneesTickets(jiraData.assignees_tickets);
            setPriorities(jiraData.priorities);
        }
        fetch();
    }, []);

    function renderToTable(e) {
        const priority_id = e.target.getAttribute("id");
        if (priority_id)
            navigate(`/jira-dashboard/priority-tickets/${priority_id}`);
    }

    return (
        <Card className="dashboard-container">
            <CustomHeader />
            <div className="dashboard-jira-container">
                {/* ── Priority KPI cards ─────────────────────────────── */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 12,
                        marginBottom: 16,
                        padding: "4px 0",
                    }}
                >
                    {priorities.length > 0 ? (
                        priorities.map((e) => {
                            if (!e.priority.trim()) return null;
                            return (
                                <div
                                    key={e.priority_id}
                                    id={e.priority_id}
                                    onClick={renderToTable}
                                    style={{
                                        flex: "1 1 140px",
                                        cursor: "pointer",
                                        background: "var(--color-bg-card)",
                                        border: "1px solid var(--color-border)",
                                        borderRadius: 12,
                                        padding: "16px 20px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 6,
                                        transition:
                                            "border-color 0.2s, transform 0.15s",
                                        userSelect: "none",
                                    }}
                                    onMouseEnter={(el) => {
                                        el.currentTarget.style.borderColor =
                                            "var(--color-primary)";
                                        el.currentTarget.style.transform =
                                            "translateY(-2px)";
                                    }}
                                    onMouseLeave={(el) => {
                                        el.currentTarget.style.borderColor =
                                            "var(--color-border)";
                                        el.currentTarget.style.transform =
                                            "translateY(0)";
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: "var(--color-text-muted)",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.06em",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        {e.priority}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 22,
                                            fontWeight: 700,
                                            color: "var(--color-text)",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        {e.openTickets}
                                        <span
                                            style={{
                                                fontSize: 13,
                                                color: "var(--color-text-muted)",
                                                fontWeight: 400,
                                            }}
                                        >
                                            {" "}
                                            / {e.tickets}
                                        </span>
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: "var(--color-text-muted)",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        open / total
                                    </span>
                                </div>
                            );
                        })
                    ) : (
                        <Skeleton
                            active
                            title={false}
                            paragraph={{ rows: 2 }}
                        />
                    )}
                </div>

                {/* ── Tabs ───────────────────────────────────────────── */}
                <Card className="box-shadow">
                    <Tabs type="card" destroyOnHidden>
                        <TabPane key={1} tab="WorkLogs & Tickets By Month">
                            <Card className="chartCard chartWithDatePicker">
                                <AssigneeTimeLog projects={projectOptions} />
                            </Card>
                        </TabPane>
                        <TabPane key={2} tab="Bugs By Project">
                            <Card className="chartCard chartWithDatePicker">
                                <BugByProject />
                            </Card>
                        </TabPane>
                        <TabPane key={3} tab="Average Worklog By Month">
                            <Card className="chartCard chartWithDatePicker">
                                <AssigneeAvgTimeLog projects={projectOptions} />
                            </Card>
                        </TabPane>
                        <TabPane key={4} tab="Tickets By Group">
                            <Card className="chartCard chartWithDatePicker">
                                <TicketByGroup projects={projectOptions} />
                            </Card>
                        </TabPane>
                        <TabPane key={5} tab="Tickets By Assignee">
                            <Card className="chartCard">
                                {assignees_tickets.length > 0 ? (
                                    <HBarChart
                                        rawData={assignees_tickets}
                                        height={600}
                                        stacked
                                    />
                                ) : (
                                    <Skeleton
                                        active
                                        title={false}
                                        paragraph={{ rows: 15, width: "100%" }}
                                    />
                                )}
                            </Card>
                        </TabPane>
                        <TabPane key={6} tab="Tickets By Project">
                            <Card className="chartCard chartWithDatePicker">
                                <TicketByProject projects={projectOptions} />
                            </Card>
                        </TabPane>
                        <TabPane key={7} tab="Tickets By Month">
                            <Card className="chartCard chartWithDatePicker">
                                <TicketByMonth projects={projectOptions} />
                            </Card>
                        </TabPane>
                        <TabPane key={8} tab="Bugs By Month">
                            <Card className="chartCard chartWithDatePicker">
                                <BugByMonth projects={projectOptions} />
                            </Card>
                        </TabPane>
                        <TabPane key={9} tab="Tickets By Age">
                            <Card className="chartCard chartWithDatePicker">
                                <TicketByAge projects={projectOptions} />
                            </Card>
                        </TabPane>
                        <TabPane key={10} tab="Tickets By Status">
                            <Card className="chartCard chartWithDatePicker">
                                <TicketByStatus projects={projectOptions} />
                            </Card>
                        </TabPane>
                    </Tabs>
                </Card>
            </div>
        </Card>
    );
}

export { JiraDashboard };
